import React, { Fragment, useEffect, useRef, useState } from 'react'
import { XIcon } from '@heroicons/react/outline'
import { Dialog, Transition } from '@headlessui/react'
import { useMediaQuery } from 'react-responsive'
import { useMeetingAppContext } from '../../MeetingAppContextDef'
import { useHallUsers } from '@/contexts/HallUsersProvider'
import useIsMobile from '../../hooks/useIsMobile'
import useIsTab from '../../hooks/useIsTab'
import { ChatPanel } from './ChatPanel'
import { ParticipantPanel } from './ParticipantPanel'
import { SpeakingSequencePanel } from './SpeakingSequencePanel'
import { TranscriptsPanel } from './TranscriptsPanel'
import { JudgmentPanel } from './JudgmentPanel'
import { SettingsPanel } from './SettingsPanel'

/** CSS selectors used by Radix popups when open */
const RADIX_OPEN_SELECTORS =
  '[data-radix-select-content][data-state="open"],[data-radix-popover-content][data-state="open"],[data-radix-dropdown-menu-content][data-state="open"],[role="listbox"][data-state="open"]'

type SideBarTabViewProps = {
  currentMode: string | null
  width: number
  height: number
  panelHeight: number
  panelHeaderHeight: number
  panelHeaderPadding: number
  panelPadding: number
  handleClose: () => void
}

const SideBarTabView: React.FC<SideBarTabViewProps> = ({
  currentMode,
  width,
  height,
  panelHeight,
  panelHeaderHeight,
  panelHeaderPadding,
  panelPadding,
  handleClose,
}) => {
  const { hallUsers } = useHallUsers()

  const panelComponents = {
    PARTICIPANTS: ParticipantPanel,
    CHAT: ChatPanel,
    'SPEAKER SCHEDULE': SpeakingSequencePanel,
    TRANSCRIPTS: TranscriptsPanel,
    JUDGMENT: JudgmentPanel,
    SETTINGS: SettingsPanel,
  } as const

  const PanelComponent = (panelComponents as any)[currentMode]
  const formattedMode = currentMode
    ? currentMode[0].toUpperCase() + currentMode.slice(1).toLowerCase()
    : ''

  return (
    <div
      id="sidePanel"
      className="dark:bg-background h-full flex flex-col"
      style={{ padding: panelPadding }}
    >
      {/* IMPORTANT: keep overflow visible on mobile or iOS kills child scroll */}
      <div className="bg-gray-750 h-full rounded-[10px] flex flex-col overflow-visible md:overflow-hidden">
        {currentMode && (
          <div
            className="flex items-center justify-between shrink-0"
            style={{
              padding: panelHeaderPadding,
              height: panelHeaderHeight - 1,
              borderBottom: '1px solid #70707033',
            }}
          >
            <p className="text-base text-gray-800 dark:text-white font-bold">
              {currentMode === 'PARTICIPANTS'
                ? `${formattedMode} (${hallUsers.length})`
                : formattedMode}
            </p>
            <button
              className="text-gray-700 dark:text-white"
              onClick={handleClose}
              style={{ margin: 0, padding: 0 }}
            >
              <XIcon className="h-5 w-5" />
            </button>
          </div>
        )}

        {/* Body: panel manages its own scroll */}
        <div className="flex-1 min-h-0">
          {PanelComponent && <PanelComponent panelHeight={panelHeight} />}
        </div>
      </div>
    </div>
  )
}

export function SidebarContainer({
  height,
  sideBarContainerWidth: initialWidth,
  mode = '',
}: {
  height: number
  sideBarContainerWidth: number
  mode?: '' | 'secondSideBar'
}) {
  const {
    raisedHandsParticipants,
    sideBarMode,
    setSideBarMode,
    secondSideBarMode,
    setSecondSideBarMode,
  } = useMeetingAppContext()
  const isMobile = useIsMobile()
  const isTab = useIsTab()
  const isLGDesktop = useMediaQuery({ minWidth: 1024, maxWidth: 1439 })
  const isXLDesktop = useMediaQuery({ minWidth: 1440 })

  const panelPadding = 8
  const panelHeaderHeight = isMobile ? 40 : isTab ? 44 : isLGDesktop ? 48 : isXLDesktop ? 52 : 0
  const panelHeaderPadding = isMobile ? 6 : isTab ? 8 : isLGDesktop ? 10 : isXLDesktop ? 12 : 0

  const currentMode = mode === 'secondSideBar' ? secondSideBarMode : sideBarMode

  /** Track whether any Radix popup is open */
  const [popupOpen, setPopupOpen] = useState(false)
  /** If a pointerdown started while popup was open, suppress the very next close */
  const suppressCloseRef = useRef(false)
  const suppressTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Observe DOM for Radix popup open/close
  useEffect(() => {
    const check = () => setPopupOpen(Boolean(document.querySelector(RADIX_OPEN_SELECTORS)))
    check()
    const mo = new MutationObserver(check)
    mo.observe(document.body, { childList: true, subtree: true, attributes: true })
    return () => mo.disconnect()
  }, [])

  // Capture pointerdown at document level to know if the tap STARTED while a popup was open.
  useEffect(() => {
    const onPointerDownCapture = () => {
      if (document.querySelector(RADIX_OPEN_SELECTORS)) {
        suppressCloseRef.current = true
        if (suppressTimeoutRef.current) clearTimeout(suppressTimeoutRef.current)
        suppressTimeoutRef.current = setTimeout(() => {
          suppressCloseRef.current = false
        }, 250)
      }
    }
    document.addEventListener('pointerdown', onPointerDownCapture, true)
    return () => {
      document.removeEventListener('pointerdown', onPointerDownCapture, true)
      if (suppressTimeoutRef.current) clearTimeout(suppressTimeoutRef.current)
    }
  }, [])

  const handleClose = () => {
    if (popupOpen || suppressCloseRef.current) return
    if (mode === 'secondSideBar') setSecondSideBarMode(null)
    else setSideBarMode(null)
  }

  const MIN_WIDTH = 350
  const MAX_WIDTH = 600
  const RESIZE_STORAGE_KEY = `sidebar-width-${mode}`

  const [width, setWidth] = useState(() => {
    const saved = localStorage.getItem(RESIZE_STORAGE_KEY)
    return saved ? parseInt(saved) : initialWidth || 360
  })

  const sidebarRef = useRef<HTMLDivElement | null>(null)
  const isResizing = useRef(false)
  const sidebarRightEdge = useRef(0)
  const saveWidthTimeout = useRef<NodeJS.Timeout | null>(null)

  const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v))

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    isResizing.current = true
    sidebarRightEdge.current =
      sidebarRef.current?.getBoundingClientRect().right ?? window.innerWidth
    ;(e.currentTarget as HTMLElement).setPointerCapture(e.pointerId)
    document.body.style.userSelect = 'none'
    document.body.style.cursor = 'col-resize'
  }

  const onPointerMove = (e: PointerEvent) => {
    if (!isResizing.current) return
    const newWidth = clamp(sidebarRightEdge.current - e.clientX, MIN_WIDTH, MAX_WIDTH)
    setWidth(newWidth)

    if (saveWidthTimeout.current) clearTimeout(saveWidthTimeout.current)
    saveWidthTimeout.current = setTimeout(() => {
      localStorage.setItem(RESIZE_STORAGE_KEY, String(newWidth))
    }, 150)
  }

  const onPointerUp = () => {
    if (!isResizing.current) return
    isResizing.current = false
    document.body.style.userSelect = ''
    document.body.style.cursor = ''
  }

  useEffect(() => {
    const move = (e: PointerEvent) => onPointerMove(e)
    window.addEventListener('pointermove', move)
    window.addEventListener('pointerup', onPointerUp)
    return () => {
      window.removeEventListener('pointermove', move)
      window.removeEventListener('pointerup', onPointerUp)
    }
  }, [])

  useEffect(() => {
    return () => {
      if (saveWidthTimeout.current) clearTimeout(saveWidthTimeout.current)
    }
  }, [])

  const commonProps = {
    raisedHandsParticipants,
    panelHeaderHeight,
    panelHeaderPadding,
    panelPadding,
    handleClose,
  }

  if (!currentMode) return null

  // ***** KEY CHANGE: compute mobile panel height from the viewport, not the background container *****
  const viewportH =
    (typeof window !== 'undefined' && (window.visualViewport?.height || window.innerHeight)) ||
    height

  const effectiveHeight = isMobile || isTab ? viewportH : height

  const panelHeight = effectiveHeight - panelHeaderHeight - panelHeaderPadding - panelPadding - 1

  return (
    <>
      {isMobile || isTab ? (
        <Transition appear show={!!currentMode} as={Fragment}>
          <Dialog as="div" className="relative" style={{ zIndex: 10000 }} onClose={handleClose}>
            {/* Backdrop — ignore pointer events so iOS doesn’t swallow touches */}
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-black bg-opacity-25 pointer-events-none" />
            </Transition.Child>

            {/* Opacity-only transition (no transforms on ancestors) */}
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0" style={{ height: 'calc(var(--vh) * 100)' }}>
                <div className="flex h-full min-h-0 items-stretch justify-stretch">
                  <Dialog.Panel
                    className="w-screen flex flex-col bg-background shadow-xl transition-all min-h-0 !transform-none pointer-events-auto z-10"
                    style={{
                      height: 'calc(var(--vh) * 100)',
                      WebkitOverflowScrolling: 'touch',
                      touchAction: 'pan-y',
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Escape' && (popupOpen || suppressCloseRef.current)) {
                        e.stopPropagation()
                        e.preventDefault()
                      }
                    }}
                    data-ios-scroll
                    role="dialog"
                  >
                    <SideBarTabView
                      currentMode={currentMode}
                      height={effectiveHeight}
                      width={initialWidth}
                      panelHeight={panelHeight}
                      {...commonProps}
                    />
                  </Dialog.Panel>
                </div>
              </div>
            </Transition.Child>
          </Dialog>
        </Transition>
      ) : (
        // DESKTOP
        <div className="flex h-full">
          {/* Left-edge handle for THIS sidebar */}
          <div
            onPointerDown={onPointerDown}
            className="w-[4px] bg-[#ccc] mr-[-4px]"
            style={{ zIndex: 10, cursor: 'col-resize', touchAction: 'none' }}
            aria-label="Resize sidebar"
            role="separator"
            aria-orientation="vertical"
          />
          <div
            ref={sidebarRef}
            id="sidebarContainer"
            className="h-full"
            style={{ width, minWidth: 350, maxWidth: 600 }}
          >
            <SideBarTabView
              currentMode={currentMode}
              height={height}
              width={width}
              panelHeight={height - panelHeaderHeight - panelHeaderPadding - panelPadding - 1}
              {...commonProps}
            />
          </div>
        </div>
      )}
    </>
  )
}
